/* Builder steps for the character's story: background and name (6),
   Experiences (7) and connections (9). */

import { useState } from 'react';
import { Pencil, RotateCcw } from 'lucide-react';
import {
  EXPERIENCE_EXAMPLES, FAMILY_NAMES, FIRST_NAMES, PLACE_NAMES, REGION_NAMES,
} from './content/sheetText';
import { ancestryFeatures, askedQuestions, DESCRIPTION_LINES, needs } from './model';
import { picks } from './GuidePage';
import { experienceList } from './leveling';
import { Chips, Panel, setAt, StepProps, TextField, toggle, toggleExample } from './BuilderParts';

const PRONOUNS = ['she/her', 'he/him', 'they/them'];

// Room for about four lines in a question's box on the printed guide.
const ANSWER_LIMIT = 240;
// The guide's longest question is 128 characters; its two printed lines shrink to fit.
const QUESTION_LIMIT = 140;

const pickOne = (list: string[]) => list[Math.floor(Math.random() * list.length)];

/** A first name and family name from the guide's name lists. */
export const randomName = () => `${pickOne(FIRST_NAMES)} ${pickOne(FAMILY_NAMES)}`;

export function BackgroundStep({ c, catalog, set }: StepProps) {
  const cls = catalog.cls(c.className);
  const desc = cls?.characterDescription;
  return (
    <>
      <Panel title="Name and pronouns" needed={needs(c, 'name')}>
        <div className="cb-row">
          <TextField label="Name" value={c.name} onChange={(name) => set({ name })} />
          <TextField label="Pronouns" value={c.pronouns} onChange={(pronouns) => set({ pronouns })} />
        </div>
        <div className="cb-actions">
          <button type="button" className="cb-btn" onClick={() => set({ name: randomName() })}>
            Suggest a name
          </button>
          <Chips label="Pronouns" options={PRONOUNS} isOn={(p) => p === c.pronouns} onPick={(pronouns) => set({ pronouns })} />
        </div>
      </Panel>

      {desc && (
        <Panel title="Character description">
          <p className="cb-hint">Choose one (or more) from each line, or write your own description.</p>
          {DESCRIPTION_LINES.map(({ key, label }) => {
            const chosen = picks(c.description[key]);
            return (
              <div key={key} className="cb-desc">
                <TextField
                  label={label}
                  maxLength={60}
                  value={c.description[key] ?? ''}
                  onChange={(v) => set({ description: { ...c.description, [key]: v } })}
                />
                <Chips
                  label={label}
                  options={desc[key]}
                  isOn={(o) => chosen.includes(o)}
                  onPick={(o) => set({ description: { ...c.description, [key]: toggle(chosen, o).join(', ') } })}
                />
              </div>
            );
          })}
        </Panel>
      )}

      {cls?.backgroundQuestions && (
        <Panel title="Background questions">
          <p className="cb-hint">Answer any of the following background questions. You can also create your own questions.</p>
          <Questions
            kind="Background question"
            guide={cls.backgroundQuestions}
            own={c.backgroundQuestions}
            answers={c.background}
            onOwn={(backgroundQuestions) => set({ backgroundQuestions })}
            onAnswer={(background) => set({ background })}
          />
          <details className="cb-group">
            <summary>Need a place name?</summary>
            <p><strong>Regions:</strong> {REGION_NAMES.join(', ')}</p>
            <p><strong>Places:</strong> {PLACE_NAMES.join(', ')}</p>
          </details>
        </Panel>
      )}
    </>
  );
}

export function ExperienceStep({ c, catalog, set }: StepProps) {
  // Taking back the Experience that had Purposeful Design's +1 takes the +1 back too.
  const pickExample = (o: string) =>
    set({ experiences: toggleExample(c.experiences, o), ...(c.experiences.indexOf(o) === c.purposeful && { purposeful: -1 }) });
  const purposeful = ancestryFeatures(c, catalog).some((f) => f.name === 'Purposeful Design');
  // Every Experience with its bonus now (Purposeful Design, advancements), as the sheet lists them.
  const bonuses = experienceList(c).map((e) => e.bonus);
  return (
    <>
      <Panel title="Your two Experiences" needed={needs(c, 'two Experiences')}>
        <div className="cb-row">
          {[0, 1].map((i) => (
            <div key={i} className="cb-exp">
              <TextField label={`Experience ${i + 1}`} value={c.experiences[i] ?? ''} onChange={(v) => set({ experiences: setAt(c.experiences, i, v) })} />
              <span className="cb-exp__mod">+{bonuses[i] ?? 2}</span>
            </div>
          ))}
        </div>
        <p className="cb-hint">Tap an example to use it (tap it again to take it back), or write your own.</p>
        {EXPERIENCE_EXAMPLES.map((group) => (
          <div key={group.label} className="cb-examples">
            <span className="cb-field__label">{group.label}</span>
            <Chips
              label={group.label}
              options={group.items}
              isOn={(o) => c.experiences.includes(o)}
              onPick={pickExample}
            />
          </div>
        ))}
      </Panel>
      <GainedExperiences c={c} set={set} bonuses={bonuses} />
      {purposeful && (
        <Panel title="Purposeful Design">
          <p className="cb-hint">Choose the Experience that best aligns with the purpose you were made for. It gains a permanent +1 bonus.</p>
          <Chips
            label="Purposeful Design"
            options={c.experiences.filter((e) => e.trim())}
            isOn={(o) => c.experiences.indexOf(o) === c.purposeful}
            onPick={(o) => set({ purposeful: c.experiences.indexOf(o) })}
          />
        </Panel>
      )}
    </>
  );
}

/** The Experiences gained at levels 2, 5 and 8 (each stored on its level up), renamed here with their bonuses beside them. */
function GainedExperiences({ c, set, bonuses }: Pick<StepProps, 'c' | 'set'> & { bonuses: number[] }) {
  const gained = c.levelUps.map((l, at) => ({ l, at })).filter(({ l }) => l.experience !== undefined);
  if (!gained.length) return null;
  const rename = (at: number, experience: string) => set({ levelUps: c.levelUps.map((l, k) => (k === at ? { ...l, experience } : l)) });
  return (
    <Panel title="Experiences from levelling up">
      <p className="cb-hint">Each tier achievement gives a new Experience at +2; advancements can raise it.</p>
      <div className="cb-row">
        {gained.map(({ l, at }, j) => (
          <div key={l.level} className="cb-exp">
            <TextField label={`Level ${l.level} Experience`} value={l.experience ?? ''} maxLength={40} onChange={(v) => rename(at, v)} />
            <span className="cb-exp__mod">+{bonuses[c.experiences.length + j] ?? 2}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function ConnectionStep({ c, catalog, set }: StepProps) {
  const cls = catalog.cls(c.className);
  if (!cls?.connectionQuestions) return <p className="cb-hint">Choose a class first: its guide has the connection questions.</p>;
  return (
    <Panel title="Connections">
      <p className="cb-hint">Ask your fellow players one of the following questions for their character to answer, or create your own questions. Note their answers here.</p>
      <Questions
        kind="Connection question"
        guide={cls.connectionQuestions}
        own={c.connectionQuestions}
        answers={c.connections}
        onOwn={(connectionQuestions) => set({ connectionQuestions })}
        onAnswer={(connections) => set({ connections })}
      />
    </Panel>
  );
}

interface QuestionsProps {
  kind: string;
  guide: string[];
  own: string[];
  answers: string[];
  onOwn: (own: string[]) => void;
  onAnswer: (answers: string[]) => void;
}

/** The guide's questions, each with its answer; a question can be reworded (printed on the guide too) and restored. */
function Questions({ kind, guide, own, answers, onOwn, onAnswer }: QuestionsProps) {
  const [editing, setEditing] = useState<{ index: number; draft: string } | null>(null);
  const asked = askedQuestions(guide, own);
  const reword = (index: number, draft: string) => {
    setEditing({ index, draft });
    onOwn(setAt(own, index, draft.trim() && draft !== guide[index] ? draft : ''));
  };
  return (
    <>
      {guide.map((original, i) => (
        <div key={original} className="cb-question" role="group" aria-label={`${kind} ${i + 1}`}>
          {editing?.index === i && (
            <TextField label="Question" maxLength={QUESTION_LIMIT} value={editing.draft} onChange={(v) => reword(i, v)} />
          )}
          <TextField label={asked[i]} multiline maxLength={ANSWER_LIMIT} value={answers[i] ?? ''} onChange={(v) => onAnswer(setAt(answers, i, v))} />
          <div className="cb-question__tools">
            <button type="button" className="cb-question__tool" onClick={() => setEditing(editing?.index === i ? null : { index: i, draft: asked[i] })}>
              <Pencil size={13} aria-hidden="true" />{editing?.index === i ? 'Done' : 'Change question'}
            </button>
            {asked[i] !== original && (
              <button
                type="button"
                className="cb-question__tool"
                onClick={() => {
                  onOwn(setAt(own, i, ''));
                  setEditing(null);
                }}
              >
                <RotateCcw size={13} aria-hidden="true" />Restore the guide's question
              </button>
            )}
          </div>
        </div>
      ))}
    </>
  );
}
